import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getWebhookLogs } from '../services/api';
import { Card } from '../components/ui/Card';
import { format } from 'date-fns';
import { CheckCircle, AlertCircle, Clock, RefreshCw, FileText, User, DollarSign, Calendar } from 'lucide-react';

const TYPE_FILTERS = [
  { value: 'all', label: 'All Events' },
  { value: 'lead_notes', label: 'Lead Note Events' },
  { value: 'quotes', label: 'Quote Events' },
  { value: 'appointments', label: 'Appointment Events' },
  { value: 'patients', label: 'Patient Events' },
];

const TYPE_BADGE: Record<string, { label: string; color: string }> = {
  lead_note_new: { label: 'Note Created', color: 'bg-purple-100 text-purple-800' },
  lead_note_update: { label: 'Note Updated', color: 'bg-indigo-100 text-indigo-800' },
  quote_new: { label: 'Quote Created', color: 'bg-emerald-100 text-emerald-800' },
  quote_update: { label: 'Quote Updated', color: 'bg-teal-100 text-teal-800' },
  appointment_new: { label: 'Appt Created', color: 'bg-amber-100 text-amber-800' },
  appointment_update: { label: 'Appt Updated', color: 'bg-orange-100 text-orange-800' },
  patient_new: { label: 'Patient New', color: 'bg-blue-100 text-blue-800' },
  patient_update: { label: 'Patient Update', color: 'bg-cyan-100 text-cyan-800' },
};

export default function WebhookLogs() {
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const { data: logs, isLoading, refetch } = useQuery({
    queryKey: ['webhook-logs', typeFilter],
    queryFn: () => getWebhookLogs(typeFilter),
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'received':
        return <Clock className="text-yellow-500" size={16} />;
      case 'processing':
        return <RefreshCw className="text-blue-500 animate-spin" size={16} />;
      case 'completed':
        return <CheckCircle className="text-green-500" size={16} />;
      case 'failed':
        return <AlertCircle className="text-red-500" size={16} />;
      default:
        return <Clock className="text-gray-500" size={16} />;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'received':
        return 'text-yellow-600';
      case 'processing':
        return 'text-blue-600';
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  const getTypeBadge = (type: string) => {
    const badge = TYPE_BADGE[type];
    if (badge) {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
          {badge.label}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
        {type}
      </span>
    );
  };

  const eventStats = logs
    ? {
        noteNew: logs.filter((l: any) => l.type === 'lead_note_new').length,
        noteUpdate: logs.filter((l: any) => l.type === 'lead_note_update').length,
        quoteNew: logs.filter((l: any) => l.type === 'quote_new').length,
        quoteUpdate: logs.filter((l: any) => l.type === 'quote_update').length,
        apptNew: logs.filter((l: any) => l.type === 'appointment_new').length,
        apptUpdate: logs.filter((l: any) => l.type === 'appointment_update').length,
        patientNew: logs.filter((l: any) => l.type === 'patient_new').length,
        patientUpdate: logs.filter((l: any) => l.type === 'patient_update').length,
      }
    : null;

  return (
    <div className="container mx-auto py-8">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-2/3">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Webhook Logs</h1>
              <p className="text-gray-600">Incoming events from 4D EMR — patient, note, quote, and appointment webhooks</p>
            </div>
            <button
              onClick={() => refetch()}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-3 py-1.5"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>

          {/* Summary chips */}
          {eventStats && (
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-1.5 bg-purple-50 text-purple-700 rounded-full px-3 py-1 text-xs font-medium">
                <FileText size={12} />
                {eventStats.noteNew} note created · {eventStats.noteUpdate} note updated
              </div>
              <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 text-xs font-medium">
                <DollarSign size={12} />
                {eventStats.quoteNew} quote created · {eventStats.quoteUpdate} quote updated
              </div>
              <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 rounded-full px-3 py-1 text-xs font-medium">
                <Calendar size={12} />
                {eventStats.apptNew} appt created · {eventStats.apptUpdate} appt updated
              </div>
              <div className="flex items-center gap-1.5 bg-blue-50 text-blue-700 rounded-full px-3 py-1 text-xs font-medium">
                <User size={12} />
                {eventStats.patientNew} patient new · {eventStats.patientUpdate} patient update
              </div>
            </div>
          )}

          {/* Type filter tabs */}
          <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => { setTypeFilter(f.value); setSelectedLog(null); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  typeFilter === f.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <RefreshCw className="animate-spin text-gray-400 mr-2" />
              <span>Loading logs...</span>
            </div>
          ) : logs && logs.length > 0 ? (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log: any) => (
                    <tr
                      key={log.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedLog?.id === log.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(log.status)}
                          <span className={`text-sm ${getStatusClass(log.status)}`}>{log.status}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTypeBadge(log.type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {format(new Date(log.created_at), 'MMM dd, HH:mm:ss')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {log.processed_at ? format(new Date(log.processed_at), 'MMM dd, HH:mm:ss') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-6 text-center">
              <p className="text-gray-500">No webhook logs found for this filter</p>
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="md:w-1/3">
          <Card>
            <div className="p-4">
              <h2 className="text-lg font-medium mb-4">Event Details</h2>
              {selectedLog ? (
                <div>
                  <div className="mb-4">
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Status</h3>
                    <div className="flex items-center gap-1.5">
                      {getStatusIcon(selectedLog.status)}
                      <span className={`${getStatusClass(selectedLog.status)}`}>{selectedLog.status}</span>
                    </div>
                  </div>
                  <div className="mb-4">
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Event Type</h3>
                    {getTypeBadge(selectedLog.type)}
                  </div>
                  <div className="mb-4">
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Source</h3>
                    <p className="text-sm">{selectedLog.source || '—'}</p>
                  </div>
                  <div className="mb-4">
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Received</h3>
                    <p className="text-sm">{format(new Date(selectedLog.created_at), 'MMM dd, yyyy HH:mm:ss')}</p>
                  </div>
                  {selectedLog.processed_at && (
                    <div className="mb-4">
                      <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Processed</h3>
                      <p className="text-sm">{format(new Date(selectedLog.processed_at), 'MMM dd, yyyy HH:mm:ss')}</p>
                    </div>
                  )}
                  {selectedLog.error && (
                    <div className="mb-4">
                      <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Error</h3>
                      <p className="text-sm text-red-600 bg-red-50 rounded p-2">{selectedLog.error}</p>
                    </div>
                  )}
                  <div className="mb-2">
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Payload</h3>
                    <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-96 border border-gray-100">
                      {JSON.stringify(selectedLog.payload, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center">Select an event to view details</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
